<template>
  <LoadingIndicator v-if="loading" message="Loading executor..." />
  <div v-else-if="loadError" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ loadError }}</div>
  <div v-else class="space-y-6">
    <div v-if="!isNew && !canEditDefinition" class="rounded-md bg-neutral-50 px-4 py-3 text-gray-900">
      <strong>Platform executor, defined by Kairon.</strong> Its model, runtime and settings are the same on every tenant.
      You decide whether it runs on this tenant, which user it writes as, and what it may write here.
    </div>
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-8 space-y-6">
        <HermesCard title="On this tenant" collapsible>
          <div class="grid grid-cols-12 gap-4">
            <label v-if="!executor.always_on" class="col-span-12 flex items-start gap-3">
              <input v-model="access.is_enabled" type="checkbox" class="mt-1 rounded border-gray-300">
              <span><span class="block text-gray-900">Enabled on this tenant</span>
                <span class="block text-gray-500">When off, nobody on this tenant can start it and its schedules stop.</span></span>
            </label>
            <label v-if="form.identity === 'self'" class="col-span-6">
              <span class="block text-gray-500 mb-2">Service user; writes are recorded as this user</span>
              <select v-model="access.service_user" :class="INPUT">
                <option :value="null">No service user</option>
                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
              <span v-if="!access.service_user" class="block text-gray-600 mt-2">Without one, scheduled runs cannot make any write.</span>
            </label>
            <label class="col-span-6">
              <span class="block text-gray-500 mb-2">Cost center for this tenant's runs</span>
              <select v-model="access.cost_center" :class="INPUT">
                <option value="">Executor default ({{ words(config.cost_center || 'customer') }})</option>
                <option v-for="c in costCenters" :key="c" :value="c">{{ words(c) }}</option>
              </select>
            </label>
          </div>
        </HermesCard>

        <fieldset :disabled="!isNew && !canEditDefinition" class="space-y-6 disabled:opacity-100">
        <HermesCard title="Identity and runtime" collapsible>
          <div class="grid grid-cols-12 gap-4">
            <label class="col-span-6">
              <span class="block text-gray-500 mb-2">Name</span>
              <input v-model="form.name" :class="INPUT">
            </label>
            <label class="col-span-6">
              <span class="block text-gray-500 mb-2">Key</span>
              <input v-model="form.key" :class="INPUT" :disabled="!isNew">
            </label>
            <label v-if="isNew && isSuperuser" class="col-span-12 flex items-start gap-3">
              <input v-model="platform" type="checkbox" class="mt-1 rounded border-gray-300">
              <span><span class="block text-gray-900">Platform executor</span>
                <span class="block text-gray-500">Available to every tenant; each tenant enables it and sets its own grants. Otherwise it belongs to this tenant only.</span></span>
            </label>
            <label class="col-span-12">
              <span class="block text-gray-500 mb-2">Description</span>
              <textarea v-model="form.description" rows="2" :class="INPUT" />
            </label>
            <fieldset class="col-span-6">
              <legend class="text-gray-500 mb-2">Runtime</legend>
              <label
v-for="r in ['single_call', 'tool_loop', 'bench']" :key="r"
                     :class="['flex gap-3 rounded-md border p-3 mb-2 cursor-pointer', form.runtime === r ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
                <input v-model="form.runtime" type="radio" :value="r" class="mt-1" @change="onRuntime">
                <span><span class="block font-medium text-gray-900">{{ words(r) }}</span>
                  <span class="block text-gray-600">{{ RUNTIME_MEANING[r] }}</span></span>
              </label>
            </fieldset>
            <fieldset class="col-span-6">
              <legend class="text-gray-500 mb-2">Identity</legend>
              <label
v-for="i in identities" :key="i.value"
                     :class="['flex gap-3 rounded-md border p-3 mb-2 cursor-pointer', form.identity === i.value ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
                <input v-model="form.identity" type="radio" :value="i.value" class="mt-1">
                <span><span class="block font-medium text-gray-900">{{ i.label }}</span>
                  <span class="block text-gray-600">{{ i.help }}</span></span>
              </label>
            </fieldset>
          </div>
        </HermesCard>

        <HermesCard title="Model" collapsible>
          <div class="grid grid-cols-12 gap-4">
            <label class="col-span-6">
              <span class="block text-gray-500 mb-2">Model</span>
              <input v-model="form.model" list="hermes-models" :class="INPUT">
              <datalist id="hermes-models">
                <option v-for="m in knownModels" :key="m" :value="m" />
              </datalist>
            </label>
            <div class="col-span-3">
              <span class="block text-gray-500 mb-2">Routes to</span>
              <span :class="['inline-flex rounded px-2 py-1', provider ? 'bg-neutral-100 text-gray-900' : 'bg-gray-100 text-gray-600']">{{ provider || 'Provider unknown' }}</span>
            </div>
            <label class="col-span-3">
              <span class="block text-gray-500 mb-2">Reasoning</span>
              <select v-model="form.reasoning" :class="INPUT">
                <option value="">Default</option>
                <option v-for="r in ['minimal', 'low', 'medium', 'high']" :key="r" :value="r">{{ r }}</option>
              </select>
            </label>
            <label class="col-span-4">
              <span class="block text-gray-500 mb-2">Max output tokens</span>
              <input v-model.number="config.max_tokens" type="number" min="1" placeholder="Model default" :class="INPUT">
            </label>
          </div>
        </HermesCard>

        <HermesCard title="Runtime settings" collapsible>
          <div class="grid grid-cols-12 gap-4">
            <template v-if="form.runtime === 'single_call'">
              <label class="col-span-4">
                <span class="block text-gray-500 mb-2">Concurrent calls</span>
                <input v-model.number="config.concurrency" type="number" min="1" :class="INPUT">
              </label>
              <label class="col-span-4 flex items-start gap-3 pt-6">
                <input v-model="config.capture_inputs" type="checkbox" class="mt-1 rounded border-gray-300">
                <span><span class="block text-gray-900">Capture inputs</span>
                  <span class="block text-gray-500">Saves each unit's exact prompt and data as input frames.</span></span>
              </label>
            </template>
            <label v-else class="col-span-4">
              <span class="block text-gray-500 mb-2">Max turns</span>
              <input v-model.number="config.max_turns" type="number" min="1" :class="INPUT">
            </label>
            <label class="col-span-4">
              <span class="block text-gray-500 mb-2">Cost center</span>
              <select v-model="config.cost_center" :class="INPUT">
                <option v-for="c in costCenters" :key="c" :value="c">{{ words(c) }}</option>
              </select>
            </label>
          </div>
        </HermesCard>

        </fieldset>

        <HermesCard title="Grants" :meta="grantTally" collapsible>
          <div v-if="form.identity === 'requester'" class="rounded-md bg-neutral-50 px-4 py-3 text-gray-900">
            This executor acts as the person who starts the run. Their own permissions replace these grants, every write is
            proposed for approval and never direct, and anything the requester cannot do is refused. Stored grants are kept but not used.
          </div>
          <template v-else>
            <p class="text-gray-700 mb-4">
              For each kind of write: <strong>Not allowed</strong> refuses it, <strong>Propose</strong> waits in Approvals for a person,
              <strong>Direct</strong> writes immediately with nobody reviewing it.
            </p>
            <div v-for="area in areas" :key="area" class="mb-4">
              <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">{{ area }}</h3>
              <table class="w-full table-fixed">
                <colgroup><col style="width:34%"><col style="width:26%"><col style="width:40%"></colgroup>
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="k in kindsIn(area)" :key="k.kind" :class="grants[k.kind] === 'direct' ? 'bg-primary-50' : ''">
                    <td :class="['py-3 pr-3 align-top', grants[k.kind] === 'direct' ? 'border-l-2 border-primary-500 pl-3' : 'pl-3']">
                      <div class="font-medium text-gray-900">{{ k.label }} <span class="text-gray-500">{{ k.kind }}</span></div>
                      <div class="text-gray-600">{{ k.description }}</div>
                      <div v-if="declaredBy[k.kind]" class="text-gray-500 mt-1">Declared by {{ declaredBy[k.kind].join(', ') }}</div>
                    </td>
                    <td class="py-3 pr-3 align-top">
                      <div class="inline-flex rounded-md border border-gray-300 overflow-hidden" role="radiogroup" :aria-label="k.label">
                        <button
v-for="m in modes" :key="m.value" type="button" role="radio" :aria-checked="(grants[k.kind] || 'none') === m.value"
                                :class="['px-3 py-1 border-r border-gray-300 last:border-r-0', (grants[k.kind] || 'none') === m.value ? m.on : 'bg-white text-gray-600 hover:bg-gray-50']"
                                @click="setMode(k, m.value)">{{ m.label }}</button>
                      </div>
                      <div v-if="changed(k.kind)" class="text-gray-500 mt-1">Was {{ modeLabel(savedGrants[k.kind]) }} · unsaved</div>
                    </td>
                    <td class="py-3 align-top">
                      <template v-if="grants[k.kind] === 'direct'">
                        <div class="font-semibold text-gray-900">Writes immediately, nobody reviews it.</div>
                        <div class="text-gray-700">{{ k.direct_consequence }}</div>
                      </template>
                      <div v-else-if="grants[k.kind] === 'propose'" class="text-gray-600">Each {{ k.label.toLowerCase() }} waits in Approvals.</div>
                      <div v-else class="text-gray-500">Refused if a run asks for it.</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </HermesCard>
      </div>

      <div class="col-span-4 space-y-6">
        <HermesCard title="Last 30 days">
          <dl class="grid grid-cols-2 gap-y-3">
            <dt class="text-gray-500">Runs</dt><dd class="text-right tabular-nums">{{ stats.runs || 0 }}</dd>
            <dt class="text-gray-500">Units</dt><dd class="text-right tabular-nums">{{ stats.units || 0 }}</dd>
            <dt class="text-gray-500">Failed units</dt><dd class="text-right tabular-nums">{{ stats.failed_units || 0 }}</dd>
            <dt class="text-gray-500">Spend</dt><dd class="text-right tabular-nums">{{ money(stats.cost_usd) }}</dd>
            <dt class="text-gray-500">Per unit</dt><dd class="text-right tabular-nums">{{ stats.units ? money(stats.cost_usd / stats.units) : '—' }}</dd>
            <dt class="text-gray-500">Tokens</dt><dd class="text-right tabular-nums">{{ tokens(stats.prompt_tokens) }} / {{ tokens(stats.completion_tokens) }}</dd>
            <dt class="text-gray-500">Last run</dt><dd class="text-right" :title="absolute(stats.last_run_at)">{{ stats.last_run_at ? relative(stats.last_run_at) : 'Never' }}</dd>
          </dl>
        </HermesCard>
        <HermesCard title="How the modes work">
          <ul class="space-y-3 text-gray-700">
            <li><strong class="text-gray-900">Not allowed</strong>: a run that asks for the write is refused before it starts.</li>
            <li><strong class="text-gray-900">Propose</strong>: the write waits in Approvals until a person approves or declines it.</li>
            <li><strong class="text-gray-900">Direct</strong>: the write happens during the run, as the service user. Undoing it is manual.</li>
          </ul>
        </HermesCard>
        <HermesCard v-if="!isNew" title="Grant changes" collapsible start-collapsed>
          <p v-if="!history.length" class="text-gray-500">No grant changes recorded.</p>
          <ul v-else class="space-y-2 text-gray-700">
            <li v-for="(h, i) in history" :key="i">
              {{ h.kind }} {{ h.change }} as {{ modeLabel(h.mode) }}
              <span class="text-gray-500">· <span class="sensitive">{{ h.by || 'system' }}</span> · <span :title="absolute(h.at)">{{ relative(h.at) }}</span></span>
            </li>
          </ul>
        </HermesCard>
      </div>
    </div>

    <HermesCard v-if="!isNew" title="Recent runs" flush>
      <template #actions>
        <router-link :to="{ name: 'AdminHermesRuns', query: { executor: executor.id } }" :class="TEXT_BUTTON">Open in Runs</router-link>
      </template>
      <table class="w-full table-fixed divide-y divide-gray-200">
        <colgroup><col style="width:7%"><col style="width:22%"><col style="width:16%"><col style="width:14%"><col style="width:11%"><col style="width:10%"><col style="width:10%"><col style="width:10%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Run</th><th :class="TH">Template</th><th :class="TH">Trigger</th><th :class="TH">Status</th>
          <th :class="TH">Progress</th><th :class="[TH, 'text-right']">Cost</th><th :class="TH">Duration</th><th :class="TH">Started</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-if="!runs.length"><td colspan="8" class="px-3 py-8 text-center text-gray-500">{{ executor.name }} hasn't run yet.</td></tr>
          <tr v-for="r in runs" :key="r.id">
            <td :class="TD"><router-link :to="{ name: 'AdminHermesRun', params: { runId: r.id } }" class="text-neutral-800">#{{ r.id }}</router-link></td>
            <td :class="TD">{{ r.template_name }} <span class="text-gray-500">v{{ r.template_version }}</span></td>
            <td :class="TD">{{ words(r.trigger) }}<span v-if="r.requested_by_name" class="text-gray-500"> · <span class="sensitive">{{ r.requested_by_name }}</span></span></td>
            <td :class="TD"><HermesBadge :value="r.status" /></td>
            <td :class="[TD, 'tabular-nums']">{{ progressLine(r) }}</td>
            <td :class="[TD, 'text-right tabular-nums']">{{ money((r.usage || {}).cost_usd) }}</td>
            <td :class="[TD, 'tabular-nums']">{{ duration(r.started_at, r.finished_at) }}</td>
            <td :class="TD" :title="absolute(r.created_at)">{{ relative(r.created_at) }}</td>
          </tr>
        </tbody>
      </table>
    </HermesCard>

    <div class="sticky bottom-0 z-10 -mx-8 border-t border-gray-200 bg-white px-8 py-3 flex items-center gap-4">
      <label v-if="isNew || canEditDefinition" class="inline-flex items-center gap-2 text-gray-700">
        <input v-model="form.is_active" type="checkbox" class="rounded border-gray-300"> Active on every tenant it is enabled on
      </label>
      <span v-if="dirtyList.length" class="text-gray-600">Unsaved: {{ dirtyList.join(', ') }}</span>
      <div v-if="saveError" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900"><strong>Not saved.</strong> {{ saveError }}</div>
      <div class="ml-auto flex items-center gap-3">
        <button type="button" :class="TEXT_BUTTON" :disabled="!dirtyList.length || saving" @click="reset">Discard</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="(!dirtyList.length && !isNew) || saving" @click="save">{{ saving ? 'Saving…' : (isNew ? 'Create executor' : 'Save changes') }}</button>
      </div>
    </div>

    <div v-if="confirmDirect" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true">
      <div class="w-[480px] rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg font-semibold text-gray-900">Allow direct {{ confirmDirect.label.toLowerCase() }} writes?</div>
        <div class="p-4 space-y-3 text-gray-700">
          <p>{{ executor.name || form.name }} will write {{ confirmDirect.label.toLowerCase() }} changes immediately, as
            <span class="sensitive">{{ serviceUserName || 'its service user' }}</span>, with nobody reviewing them. Scheduled runs are included.</p>
          <p>{{ confirmDirect.direct_consequence }}</p>
          <p v-if="lastUnits">Its last run covered {{ lastUnits }} units, so one run could make up to {{ lastUnits }} of these changes. Undoing them is manual.</p>
        </div>
        <div class="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
          <button type="button" :class="SECONDARY_BUTTON" @click="keepPropose">Keep as Propose</button>
          <button type="button" :class="PRIMARY_BUTTON" @click="allowDirect">Allow direct writes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesBadge from './HermesBadge.vue';
import HermesCard from './HermesCard.vue';
import {
  API, INPUT, PRIMARY_BUTTON, RUNTIME_MEANING, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
  absolute, duration, errorOf, money, progressLine, providerFor, relative, tokens, words,
} from './hermes';

const BLANK = { key: '', name: '', description: '', runtime: 'single_call', model: 'gpt-5.6-luna', reasoning: '', identity: 'self', is_active: true };
const ACCESS_BLANK = { is_enabled: true, service_user: null, cost_center: '' };
const RUNTIME_KEYS = { single_call: ['concurrency', 'capture_inputs', 'cost_center', 'max_tokens'], tool_loop: ['max_turns', 'cost_center', 'max_tokens'], bench: ['cost_center'] };

export default {
  name: 'HermesExecutorDetail',
  components: { LoadingIndicator, HermesBadge, HermesCard },
  emits: ['crumb'],
  data() {
    return {
      loading: true, loadError: '', saving: false, saveError: '',
      executor: {}, form: { ...BLANK }, config: {}, grants: {}, savedGrants: {}, access: { ...ACCESS_BLANK }, platform: false,
      kinds: [], areas: [], templates: [], users: [], runs: [], history: [], confirmDirect: null,
      identities: [
        { value: 'self', label: 'Acts as itself', help: 'Writes as its service user, limited by the grants below.' },
        { value: 'requester', label: 'Acts as the requester', help: "Borrows the starting person's permissions; every write is proposed." },
      ],
      modes: [
        { value: 'none', label: 'Not allowed', on: 'bg-gray-100 text-gray-900' },
        { value: 'propose', label: 'Propose', on: 'bg-neutral-100 text-gray-900' },
        { value: 'direct', label: 'Direct', on: 'bg-primary-100 text-gray-900 font-semibold' },
      ],
      costCenters: ['customer', 'internal_engineering', 'internal_ops', 'etl'],
      knownModels: ['gpt-6-sol', 'gpt-6-luna', 'gpt-5.6-luna', 'gpt-5.6-terra', 'gpt-5.6-sol', 'gpt-5.5', 'claude-opus-4-8', 'claude-sonnet-4-6', 'claude-haiku-4-5', 'vertex/claude-opus-4-8', 'gemini-3.8-flash'],
      INPUT, PRIMARY_BUTTON, RUNTIME_MEANING, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    isNew() {
      return this.$route.params.executorKey === 'new';
    },
    isSuperuser() {
      return !!(this.$store.getters['auth/current_user'] || {}).is_superuser;
    },
    canEditDefinition() {
      return this.isNew || !!this.executor.can_edit_definition;
    },
    savedAccess() {
      return { is_enabled: !!this.executor.enabled, service_user: this.executor.service_user ?? null, cost_center: this.executor.cost_center || '' };
    },
    definitionDirty() {
      if (this.isNew) return false;
      const fields = ['name', 'description', 'runtime', 'model', 'reasoning', 'identity', 'is_active'].some((f) => (this.form[f] ?? '') !== (this.executor[f] ?? ''));
      return fields || JSON.stringify(this.cleanConfig) !== JSON.stringify(this.executor.config || {});
    },
    accessDirty() {
      return !this.isNew && JSON.stringify(this.access) !== JSON.stringify(this.savedAccess);
    },
    grantsDirty() {
      return Object.keys({ ...this.grants, ...this.savedGrants }).some((k) => this.changed(k));
    },
    provider() {
      return providerFor(this.form.model);
    },
    stats() {
      return this.executor.stats_30d || {};
    },
    lastUnits() {
      const run = this.runs.find((r) => (r.progress || {}).units);
      return run ? run.progress.units : 0;
    },
    serviceUserName() {
      const u = this.users.find((x) => x.id === this.access.service_user);
      return u ? u.name : this.executor.service_user_name;
    },
    declaredBy() {
      const out = {};
      this.templates.forEach((t) => (t.effects || []).forEach((k) => { (out[k] = out[k] || []).push(t.name); }));
      return out;
    },
    grantTally() {
      if (this.form.identity === 'requester') return "Requester's own permissions";
      const vals = Object.values(this.grants);
      return `${vals.filter((v) => v === 'direct').length} direct · ${vals.filter((v) => v === 'propose').length} propose`;
    },
    cleanConfig() {
      const out = {};
      (RUNTIME_KEYS[this.form.runtime] || []).forEach((k) => {
        if (this.config[k] !== undefined && this.config[k] !== '' && this.config[k] !== null) out[k] = this.config[k];
      });
      return out;
    },
    dirtyList() {
      if (this.isNew) return [];
      const out = [];
      ['name', 'description', 'runtime', 'model', 'reasoning', 'identity', 'is_active'].forEach((f) => {
        if ((this.form[f] ?? '') !== (this.executor[f] ?? '')) out.push(words(f));
      });
      if (JSON.stringify(this.cleanConfig) !== JSON.stringify(this.executor.config || {})) out.push('settings');
      if (this.access.is_enabled !== this.savedAccess.is_enabled) out.push(this.access.is_enabled ? 'enable here' : 'disable here');
      if (this.access.service_user !== this.savedAccess.service_user) out.push('service user');
      if (this.access.cost_center !== this.savedAccess.cost_center) out.push('cost center');
      Object.keys({ ...this.grants, ...this.savedGrants }).forEach((k) => {
        if (this.changed(k)) out.push(`${k} ${this.modeLabel(this.savedGrants[k])} → ${this.modeLabel(this.grants[k])}`);
      });
      return out;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute, duration, money, progressLine, relative, tokens, words,
    async load() {
      this.loading = true;
      try {
        const calls = [this.$axios.get(`${API}/write-kinds/`), this.$axios.get(`${API}/templates/`), this.$axios.get('/api/user/', { params: { include_staff: 'true' } }).catch(() => ({ data: [] }))];
        if (!this.isNew) {
          const key = this.$route.params.executorKey;
          calls.push(this.$axios.get(`${API}/executors/${key}/`), this.$axios.get(`${API}/runs/`, { params: { executor_key: key, limit: 10 } }),
            this.$axios.get(`${API}/executors/${key}/grant-history/`).catch(() => ({ data: [] })));
        }
        const [kinds, templates, users, executor, runs, history] = await Promise.all(calls);
        this.kinds = kinds.data.kinds;
        this.areas = kinds.data.areas.filter((a) => this.kinds.some((k) => k.area === a));
        this.templates = templates.data;
        const list = Array.isArray(users.data) ? users.data : (users.data.results || []);
        this.users = list.map((u) => ({ id: u.id, name: u.full_name || u.email }));
        if (!this.isNew) {
          this.executor = executor.data;
          this.runs = runs.data.results || [];
          this.history = history.data;
        }
        this.reset();
        this.$emit('crumb', this.isNew ? 'New executor' : this.executor.name);
      } catch (e) {
        this.loadError = e?.response?.status === 404 ? 'No executor with that key on this tenant.' : errorOf(e, 'The executor could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    reset() {
      const src = this.isNew ? BLANK : this.executor;
      this.form = Object.fromEntries(Object.keys(BLANK).map((k) => [k, src[k] ?? BLANK[k]]));
      this.config = { concurrency: 6, capture_inputs: true, cost_center: 'customer', max_turns: 80, ...(this.isNew ? {} : this.executor.config) };
      this.access = this.isNew ? { ...ACCESS_BLANK } : { ...this.savedAccess };
      this.savedGrants = Object.fromEntries((this.executor.grants || []).map((g) => [g.kind, g.mode]));
      this.grants = { ...this.savedGrants };
      this.saveError = '';
    },
    onRuntime() {
      this.config = { ...this.config };
    },
    kindsIn(area) {
      return this.kinds.filter((k) => k.area === area);
    },
    modeLabel(mode) {
      return { direct: 'Direct', propose: 'Propose' }[mode] || 'Not allowed';
    },
    changed(kind) {
      return (this.grants[kind] || 'none') !== (this.savedGrants[kind] || 'none');
    },
    setMode(kind, mode) {
      if (mode === 'direct' && this.savedGrants[kind.kind] !== 'direct' && this.grants[kind.kind] !== 'direct') {
        this.confirmDirect = kind;
        return;
      }
      const next = { ...this.grants };
      if (mode === 'none') delete next[kind.kind];
      else next[kind.kind] = mode;
      this.grants = next;
    },
    keepPropose() {
      this.grants = { ...this.grants, [this.confirmDirect.kind]: 'propose' };
      this.confirmDirect = null;
    },
    allowDirect() {
      this.grants = { ...this.grants, [this.confirmDirect.kind]: 'direct' };
      this.confirmDirect = null;
    },
    async save() {
      this.saving = true;
      this.saveError = '';
      const body = { ...this.form, config: this.cleanConfig };
      try {
        let key = this.executor.key;
        if (this.isNew) {
          key = this.form.key;
          await this.$axios.post(`${API}/executors/`, { ...body, platform: this.platform });
        } else if (this.definitionDirty && this.canEditDefinition) {
          delete body.key;
          await this.$axios.patch(`${API}/executors/${key}/`, body);
        }
        if (this.isNew || this.accessDirty) await this.$axios.put(`${API}/executors/${key}/access/`, this.access);
        if (this.grantsDirty) {
          await this.$axios.put(`${API}/executors/${key}/grants/`, { grants: Object.entries(this.grants).map(([kind, mode]) => ({ kind, mode })) });
        }
        if (this.isNew) this.$router.replace({ name: 'AdminHermesExecutor', params: { executorKey: key } });
        else await this.load();
      } catch (e) {
        this.saveError = errorOf(e, 'The platform did not accept the change.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
